/**
 * The screen's own name, and one sentence saying what it is for.
 *
 * Until v4 of the design contract a screen began at its first panel. The only
 * thing naming the route was the mark in the navigation 82px above it, so a
 * screenshot of anything below the fold could not be identified, and four
 * screens that shared a panel vocabulary were told apart by their content
 * alone. A section that does not say its name relies on the reader having
 * watched themselves arrive.
 *
 * The title is the fixed section name from §1 of the contract. Not a
 * rephrasing, not a breadcrumb, not a count: the word in the navigation and
 * the word at the top of the page are the same word, or the reader has to
 * decide whether they are in the same place.
 *
 * The description is written once and does not move with the data. A line that
 * changes with the account is a figure, and figures belong in the lead strip
 * below it.
 *
 * Contract §4.1.
 */

type Props = {
  readonly title: string;
  readonly description: string;
  /**
   * Right-aligned controls. Empty in V1 — every screen is read-only, and a
   * control that looks live and does nothing is a promise the product cannot
   * keep. The slot exists so that the day a screen earns an action, the action
   * has one place to go rather than four.
   */
  readonly actions?: React.ReactNode | undefined;
  readonly testId?: string | undefined;
};

export function PageHeader({
  title,
  description,
  actions,
  testId,
}: Props): React.JSX.Element {
  return (
    <div
      className="flex flex-wrap items-end justify-between gap-3"
      data-testid={testId}
    >
      <div className="min-w-0">
        {/* The one hero-sized thing on a screen whose subject is a table. It
            is a name rather than a reading, so it does not spend the screen's
            single hero figure — see §3. */}
        <h1 className="text-hero-metric-mobile text-text-primary sm:text-hero-metric">
          {title}
        </h1>
        <p className="mt-1 text-body-main text-text-secondary">{description}</p>
      </div>
      {actions !== undefined && (
        <div className="flex items-center gap-2">{actions}</div>
      )}
    </div>
  );
}
